import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Save, ArrowLeft, AlertTriangle, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { SectionTitle, Badge } from "@/components/Bits";

const EXIT_MODES = [
  {
    value: "manual",
    label: "Manual Exit Only",
    blurb: "Bot enters automatically, exits manually. No TP, SL, or time-based auto-close. Kill switch and risk emergency still close everything.",
  },
  {
    value: "tp_sl",
    label: "Take Profit / Stop Loss",
    blurb: "Auto-close on TP or SL. Each can be enabled independently.",
  },
  {
    value: "time",
    label: "Time-Based Exit",
    blurb: "Auto-close after a fixed duration in the market.",
  },
  {
    value: "opposite_signal",
    label: "Opposite Signal Exit",
    blurb: "Close current position when an opposite-direction signal fires (handled at entry time, not by exit poller).",
  },
  {
    value: "combined",
    label: "Combined Exit Rules",
    blurb: "Multiple exit conditions active simultaneously. Whichever triggers first closes the position.",
  },
];

const DEFAULT_EXIT = {
  mode: "tp_sl",
  take_profit_enabled: true,
  take_profit_percent: 1.0,
  stop_loss_enabled: true,
  stop_loss_percent: 0.5,
  max_duration_enabled: false,
  max_duration_minutes: 0,
  close_on_opposite_signal: false,
  // Composite "stale loser" exit. Independent of `mode` — runs in
  // ALL modes (including manual / opposite_signal) whenever its own
  // `enabled` is true. Disabled by default so existing profiles do
  // not change behavior on upgrade.
  deterioration_exit: {
    enabled: false,
    min_duration_minutes: 360,
    pnl_threshold_percent: -0.10,
    no_new_mfe_minutes: 180,
  },
  // MFE-relative trailing exit. Independent of `mode` — runs in ALL
  // modes whenever its own `enabled` is true. Priority slot #5 (above
  // Deterioration, below MaxDuration). Disabled by default.
  mfe_giveback_trailing: {
    enabled: false,
    activation_mfe_percent: 1.00,
    giveback_percent: 50,
  },
};

export default function ExitRules() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(DEFAULT_EXIT);
  const [original, setOriginal] = useState(DEFAULT_EXIT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [list, active] = await Promise.all([
          api.strategyProfiles(),
          api.strategyActive(),
        ]);
        setProfiles(list || []);
        setActiveId(active?.active_strategy_id || null);
        const fromUrl = searchParams.get("profile_id");
        const initial = (list || []).find((p) => p.id === fromUrl)
          || (list || []).find((p) => p.id === active?.active_strategy_id)
          || (list || [])[0] || null;
        if (initial) {
          setSelectedId(initial.id);
          const er = { ...DEFAULT_EXIT, ...(initial.exit_rules || {}) };
          setDraft(er);
          setOriginal(er);
          if (!fromUrl) setSearchParams({ profile_id: initial.id }, { replace: true });
        }
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelectProfile = (id) => {
    const p = profiles.find((x) => x.id === id);
    if (!p) return;
    setSelectedId(id);
    const er = { ...DEFAULT_EXIT, ...(p.exit_rules || {}) };
    setDraft(er);
    setOriginal(er);
    setSearchParams({ profile_id: id }, { replace: true });
  };

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(original),
    [draft, original]
  );

  const onSave = async () => {
    if (!selectedId) return;
    try {
      setSaving(true);
      const updated = await api.strategyUpdate(selectedId, { exit_rules: draft });
      const er = { ...DEFAULT_EXIT, ...(updated.exit_rules || {}) };
      setDraft(er);
      setOriginal(er);
      const fresh = await api.strategyProfiles();
      setProfiles(fresh || []);
      toast.success("Exit rules saved" + (selectedId === activeId ? " · live logic updated" : ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-[#A0A0AB]">Loading…</div>;
  if (profiles.length === 0) {
    return (
      <div className="p-6 max-w-3xl space-y-4">
        <SectionTitle>Exit Rules</SectionTitle>
        <div className="bg-[#0F0F14] border border-border rounded-sm p-6 text-sm text-[#A0A0AB]">
          No strategy profiles yet. Create one from{" "}
          <button className="text-[#00E676] underline decoration-dotted hover:decoration-solid"
            onClick={() => navigate("/strategies")}>Strategy Profiles</button>.
        </div>
      </div>
    );
  }

  const isActive = selectedId && selectedId === activeId;
  const mode = draft.mode;
  // Stop Loss and Take Profit are independent safety exits — they run
  // in EVERY Primary Exit Mode whenever their own toggle is True.
  // Max Duration is also toggle-only. We therefore show the TP/SL
  // and Time-Based sections regardless of `mode`.
  const showTpSl = true;
  const showTime = true;
  // Opposite-signal toggle lives ONLY in combined mode. In standalone
  // `opposite_signal` mode the behavior is implied by the mode — no toggle.
  const showOppositeToggle = mode === "combined";
  const standaloneOpposite = mode === "opposite_signal";
  const isManual = mode === "manual";
  // Hard-warning only when the operator picked a mode that USED to be
  // governed by TP/SL exclusively (tp_sl) and disabled both. In other
  // modes, TP/SL off is a perfectly valid configuration.
  const tpSlBothOff =
    mode === "tp_sl" && !draft.take_profit_enabled && !draft.stop_loss_enabled;

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <SectionTitle
        sub="Edit auto-exit behavior of the selected Strategy Profile. Each strategy has its own self-contained exit rules; the active strategy's rules apply on the next validation cycle. Kill switch and risk-emergency always override these — even in Manual mode."
        right={
          <Button variant="outline" size="sm" onClick={() => navigate("/strategies")}
            className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] text-xs">
            <ArrowLeft size={12} strokeWidth={2} className="mr-1" /> All strategies
          </Button>
        }
      >
        Exit Rules — Strategy Editor
      </SectionTitle>

      {/* Strategy selector */}
      <div
        data-testid="strategy-selector-card"
        className={`rounded-sm border p-4 ${
          isActive ? "border-[#00E676]/40 bg-[#00E676]/5" : "border-border bg-[#0F0F14]"
        }`}
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Label className="text-[10px] uppercase tracking-widest text-[#71717A]">Editing Strategy</Label>
            <Select value={selectedId || undefined} onValueChange={onSelectProfile}>
              <SelectTrigger data-testid="strategy-selector" className="bg-[#1A1A22] border-border rounded-sm min-w-[260px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {profiles.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} {p.id === activeId ? "• ACTIVE" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isActive ? <Badge kind="on" testid="active-strategy-badge">ACTIVE STRATEGY</Badge> : null}
          </div>
          {dirty ? (
            <span className="text-xs text-[#F59E0B] flex items-center gap-1">
              <AlertTriangle size={12} strokeWidth={2} /> unsaved changes
            </span>
          ) : null}
        </div>
      </div>

      {/* Exit mode selector */}
      <div className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-4">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Primary Exit Mode</div>
        <div className="space-y-1">
          <Select value={draft.mode} onValueChange={(v) => {
            // Auto-align the toggle when switching modes so the persisted
            // profile JSON is always internally consistent with the mode:
            //   opposite_signal → toggle=true (implied by mode)
            //   manual / tp_sl / time → toggle=false (ignored by engine)
            //   combined → keep current toggle value (user explicitly controls it)
            const next = { ...draft, mode: v };
            if (v === "opposite_signal") next.close_on_opposite_signal = true;
            else if (v === "manual" || v === "tp_sl" || v === "time") next.close_on_opposite_signal = false;
            setDraft(next);
          }}>
            <SelectTrigger data-testid="field-exit-mode" className="bg-[#1A1A22] border-border rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXIT_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-xs text-[#A0A0AB] mt-2 leading-relaxed">
            {EXIT_MODES.find((m) => m.value === draft.mode)?.blurb}
          </div>
          <div
            data-testid="exit-mode-composition-hint"
            className="text-[11px] text-[#71717A] mt-2 leading-relaxed border-l-2 border-[#3B82F6]/40 pl-2"
          >
            <span className="text-white">Stop Loss and Take Profit are independent safety exits.</span>{" "}
            If enabled, they run in EVERY Primary Exit Mode (manual, opposite_signal, tp_sl, time, combined).
            Max Duration is also toggle-only. Live engine priority order:{" "}
            <span className="font-mono text-[#A0A0AB]">
              Kill switch &gt; Stop Loss &gt; Take Profit &gt; Max Duration &gt; MFE Giveback &gt; Deterioration &gt; Opposite Signal
            </span>.
          </div>
        </div>
      </div>

      {/* Manual mode notice */}
      {isManual ? (
        <div data-testid="manual-mode-notice" className="rounded-sm border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-4 flex items-start gap-3">
          <ShieldAlert size={16} strokeWidth={2} className="text-[#F59E0B] shrink-0 mt-0.5" />
          <div className="text-xs">
            <div className="font-semibold text-[#F59E0B] uppercase tracking-wider">Manual Primary Mode</div>
            <div className="text-[#A0A0AB] mt-1 leading-relaxed">
              No opposite-signal auto-close in this mode — the bot will not flip on contra signals.
              However, <span className="text-white">Stop Loss, Take Profit, Max Duration, MFE Giveback and Deterioration</span> still run
              whenever their own toggles are enabled. Kill switch and risk emergency stops always close everything regardless.
            </div>
          </div>
        </div>
      ) : null}

      {/* Both TP+SL off warning */}
      {tpSlBothOff ? (
        <div data-testid="tp-sl-both-off-warning" className="rounded-sm border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-3 flex items-start gap-2 text-xs text-[#FF3B30]">
          <AlertTriangle size={14} strokeWidth={2} className="shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold uppercase tracking-wider">No exits enabled</span>
            <span className="text-[#FF3B30]/80 normal-case ml-2">
              Both TP and SL are off in {mode === "combined" ? "Combined" : "TP/SL"} mode. Enable at least one or switch to Manual mode if intentional.
            </span>
          </div>
        </div>
      ) : null}

      {/* TP/SL fields */}
      {showTpSl ? (
        <div className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-4">
          <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Take Profit / Stop Loss</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs">Take Profit enabled</Label>
                <Switch
                  data-testid="field-tp-enabled"
                  checked={draft.take_profit_enabled}
                  onCheckedChange={(v) => setDraft({ ...draft, take_profit_enabled: v })}
                />
              </div>
              <Input
                data-testid="field-tp-percent"
                type="number" step="0.1" min="0" max="100"
                disabled={!draft.take_profit_enabled}
                value={draft.take_profit_percent}
                onChange={(e) => setDraft({ ...draft, take_profit_percent: Number(e.target.value) })}
                className="bg-[#1A1A22] border-border rounded-sm font-mono disabled:opacity-50"
                placeholder="e.g. 1.0"
              />
              <div className="text-[10px] text-[#71717A]">Percent (leverage-adjusted) above entry to auto-close.</div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs">Stop Loss enabled</Label>
                <Switch
                  data-testid="field-sl-enabled"
                  checked={draft.stop_loss_enabled}
                  onCheckedChange={(v) => setDraft({ ...draft, stop_loss_enabled: v })}
                />
              </div>
              <Input
                data-testid="field-sl-percent"
                type="number" step="0.1" min="0" max="100"
                disabled={!draft.stop_loss_enabled}
                value={draft.stop_loss_percent}
                onChange={(e) => setDraft({ ...draft, stop_loss_percent: Number(e.target.value) })}
                className="bg-[#1A1A22] border-border rounded-sm font-mono disabled:opacity-50"
                placeholder="e.g. 0.5"
              />
              <div className="text-[10px] text-[#71717A]">Percent (leverage-adjusted) below entry to auto-close.</div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Time-based fields */}
      {showTime ? (
        <div className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-4">
          <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Time-Based Exit</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs">Max duration enabled</Label>
                <Switch
                  data-testid="field-duration-enabled"
                  checked={draft.max_duration_enabled}
                  onCheckedChange={(v) => setDraft({ ...draft, max_duration_enabled: v })}
                />
              </div>
              <Input
                data-testid="field-duration-minutes"
                type="number" min="0" max="10000"
                disabled={!draft.max_duration_enabled}
                value={draft.max_duration_minutes}
                onChange={(e) => setDraft({ ...draft, max_duration_minutes: Number(e.target.value) })}
                className="bg-[#1A1A22] border-border rounded-sm font-mono disabled:opacity-50"
              />
              <div className="text-[10px] text-[#71717A]">Auto-close any position older than N minutes.</div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Opposite-signal fields: toggle only in combined mode.
          In standalone `opposite_signal` mode the behavior is implied
          by the mode itself — we show only an explanation, no duplicate toggle. */}
      {standaloneOpposite ? (
        <div className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Opposite Signal Exit</div>
          <div className="text-[11px] text-[#A0A0AB] leading-relaxed" data-testid="opposite-mode-explanation">
            When an opposite-direction signal fires while a position is open, the bot closes the current
            position before opening the new one. This is the selected Exit Mode — no additional toggle
            is needed.
          </div>
        </div>
      ) : showOppositeToggle ? (
        <div className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-4">
          <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Opposite Signal Exit</div>
          <div className="flex items-center justify-between gap-2 max-w-md">
            <Label className="text-xs">Close on opposite signal</Label>
            <Switch
              data-testid="field-close-opposite"
              checked={draft.close_on_opposite_signal}
              onCheckedChange={(v) => setDraft({ ...draft, close_on_opposite_signal: v })}
            />
          </div>
          <div className="text-[11px] text-[#A0A0AB] leading-relaxed">
            When an opposite-direction signal fires while a position is open, the bot closes the current
            position before opening the new one.
          </div>
        </div>
      ) : null}

      {/* Deterioration Exit — composite stale-loser rule. Runs in
          ALL modes (including manual / opposite_signal) when enabled —
          this is intentional so operators can combine "manual / no
          fixed TP" with a deterioration cap. Disabled by default. */}
      <div className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Deterioration Exit</div>
            <div className="text-[11px] text-[#A0A0AB] leading-relaxed mt-1 max-w-2xl">
              Close trades that have been open too long, are currently negative, and have stopped making new
              MFE. Runs in every mode — including Manual — so you can let winners run with no fixed TP while
              still cutting stale losers. Disabled by default; takes effect on the next validation cycle.
            </div>
          </div>
          <Switch
            data-testid="field-deterioration-enabled"
            checked={!!draft.deterioration_exit?.enabled}
            onCheckedChange={(v) =>
              setDraft({
                ...draft,
                deterioration_exit: {
                  ...(draft.deterioration_exit || DEFAULT_EXIT.deterioration_exit),
                  enabled: v,
                },
              })
            }
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Minimum trade duration (min)</Label>
            <Input
              data-testid="field-deterioration-min-duration"
              type="number" min="1" max="100000" step="1"
              disabled={!draft.deterioration_exit?.enabled}
              value={draft.deterioration_exit?.min_duration_minutes ?? 360}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  deterioration_exit: {
                    ...(draft.deterioration_exit || DEFAULT_EXIT.deterioration_exit),
                    min_duration_minutes: Number(e.target.value),
                  },
                })
              }
              className="bg-[#1A1A22] border-border rounded-sm font-mono disabled:opacity-50"
            />
            <div className="text-[10px] text-[#71717A]">Rule starts evaluating after this duration.</div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">PnL threshold (%)</Label>
            <Input
              data-testid="field-deterioration-pnl-threshold"
              type="number" min="-100" max="100" step="0.01"
              disabled={!draft.deterioration_exit?.enabled}
              value={draft.deterioration_exit?.pnl_threshold_percent ?? -0.10}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  deterioration_exit: {
                    ...(draft.deterioration_exit || DEFAULT_EXIT.deterioration_exit),
                    pnl_threshold_percent: Number(e.target.value),
                  },
                })
              }
              className="bg-[#1A1A22] border-border rounded-sm font-mono disabled:opacity-50"
              placeholder="-0.10"
            />
            <div className="text-[10px] text-[#71717A]">
              Price-based (not leverage-adjusted). Negative = require losing trade.
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">No new MFE for (min)</Label>
            <Input
              data-testid="field-deterioration-no-mfe"
              type="number" min="1" max="100000" step="1"
              disabled={!draft.deterioration_exit?.enabled}
              value={draft.deterioration_exit?.no_new_mfe_minutes ?? 180}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  deterioration_exit: {
                    ...(draft.deterioration_exit || DEFAULT_EXIT.deterioration_exit),
                    no_new_mfe_minutes: Number(e.target.value),
                  },
                })
              }
              className="bg-[#1A1A22] border-border rounded-sm font-mono disabled:opacity-50"
            />
            <div className="text-[10px] text-[#71717A]">Close if no new MFE has been made for this long.</div>
          </div>
        </div>
      </div>

      {/* MFE Giveback Trailing — profit-protection exit. Runs in ALL
          modes (priority slot #5, above Deterioration). After a trade
          reaches a minimum favorable excursion, close it if it gives
          back too much of that move. Disabled by default. */}
      <div className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#71717A]">MFE Giveback Trailing</div>
            <div className="text-[11px] text-[#A0A0AB] leading-relaxed mt-1 max-w-2xl">
              After a trade reaches a minimum MFE, close it if it gives back too much of that favorable move.
              Price-based (not leverage-adjusted). Runs in every mode — including Manual — and evaluates
              BEFORE Deterioration so profit-protection wins same-cycle ties. Disabled by default.
            </div>
          </div>
          <Switch
            data-testid="field-mfe-giveback-enabled"
            checked={!!draft.mfe_giveback_trailing?.enabled}
            onCheckedChange={(v) =>
              setDraft({
                ...draft,
                mfe_giveback_trailing: {
                  ...(draft.mfe_giveback_trailing || DEFAULT_EXIT.mfe_giveback_trailing),
                  enabled: v,
                },
              })
            }
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Activate after MFE (%)</Label>
            <Input
              data-testid="field-mfe-giveback-activation"
              type="number" min="0" max="100" step="0.01"
              disabled={!draft.mfe_giveback_trailing?.enabled}
              value={draft.mfe_giveback_trailing?.activation_mfe_percent ?? 1.00}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  mfe_giveback_trailing: {
                    ...(draft.mfe_giveback_trailing || DEFAULT_EXIT.mfe_giveback_trailing),
                    activation_mfe_percent: Number(e.target.value),
                  },
                })
              }
              className="bg-[#1A1A22] border-border rounded-sm font-mono disabled:opacity-50"
              placeholder="1.00"
            />
            <div className="text-[10px] text-[#71717A]">
              Trailing only arms after MFE reaches this percentage (price-based).
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Allowed giveback (%)</Label>
            <Input
              data-testid="field-mfe-giveback-percent"
              type="number" min="0" max="100" step="0.1"
              disabled={!draft.mfe_giveback_trailing?.enabled}
              value={draft.mfe_giveback_trailing?.giveback_percent ?? 50}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  mfe_giveback_trailing: {
                    ...(draft.mfe_giveback_trailing || DEFAULT_EXIT.mfe_giveback_trailing),
                    giveback_percent: Number(e.target.value),
                  },
                })
              }
              className="bg-[#1A1A22] border-border rounded-sm font-mono disabled:opacity-50"
              placeholder="50"
            />
            <div className="text-[10px] text-[#71717A]">
              Close when current PnL falls this much below achieved MFE.
              Example: MFE=2% with 50% giveback closes at PnL=1%.
            </div>
          </div>
        </div>
      </div>

      {/* Save bar */}
      <div className="sticky bottom-4 bg-[#05050A]/80 backdrop-blur-sm border border-border rounded-sm p-3 flex items-center justify-between gap-3">
        <div className="text-xs text-[#A0A0AB]">
          {isActive ? (
            <span className="text-[#00E676]">Saving updates the live validation engine on the next cycle.</span>
          ) : (
            <>This profile is inactive. Save stores changes; activate it to affect automated execution.</>
          )}
        </div>
        <Button
          data-testid="save-exit-rules-btn"
          onClick={onSave}
          disabled={saving || !dirty}
          className="rounded-sm bg-[#00E676] hover:bg-[#00C766] text-black font-semibold disabled:opacity-50"
        >
          <Save size={14} strokeWidth={2} className="mr-1" />
          {saving ? "Saving…" : dirty ? "Save changes" : "No changes"}
        </Button>
      </div>
    </div>
  );
}
